import moment, {type Moment} from 'moment-timezone';
import i18next from 'i18next';

import {isNonEmpty, type NonEmptyArray} from 'web-app/util/non-empty-array';
import type {
    EnableBillPayerAutomaticInvoiceSendingMutationVariables,
    UpdateBillingFrequenciesMutationVariables,
} from 'web-app/react/profile/bill-payer-v2/__generated__/queries.api-types';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {exhaustiveCheck} from '@famly/stat_ts-utils_exhaustive-check';

import type * as ApiTypes from '../api-types';
import {isAutomaticPricingGroup, isEndOfPeriodDate, isFirstDayOfBillingPeriod, sortDiscounts} from '../helpers/helpers';
import {
    transformBillingFrequencyToApiSchedule,
    transformChildrenAndFeeToApiAdhocPurchases,
    transformDiscountsToApiDiscounts,
    transformFeesToApiProductBookings,
    transformPlanPricingGroupIdToApiPlanPricingGroupId,
    transformProratedItemsToApiProductCreations,
    transformProratedTuitionsToApiSessionPackageCreations,
    transformTuitionsToApiSessionBookings,
    transformTuitionsToApiSessionBookingsForEditView,
    transformTuitionsWithOverriddenPriceToApiSessionPackageCreation,
    type ExecuteTransformChildrenAndFeeToApiAdhocPurchases,
} from '../transformers/api-transformers';
import {getStartDateForEndPeriod, transformEndDateToPlanEndDate} from '../transformers/end-date';
import type * as Types from '../types';
import {type AdhocPurchasePricingGroups, type PricingGroupsForChild} from './use-pricing-group-for-adhoc-purchase';
import {biweeklyBillingStartDate} from '../helpers/create-billing-periods';

type Args = SubmissionDataArgs & {
    generateUUID: () => string;
};
const useSubmissionData = ({
    siteId,
    state,
    pricingGroupOptions,
    billingProfiles,
    adhocPurchasePricingGroups,
    generateUUID,
    initialPlanTuitions,
}: Args): ReturnType<typeof submissionData> => {
    return submissionData({
        generateUUID,
        siteId,
        state,
        adhocPurchasePricingGroups,
        pricingGroupOptions,
        billingProfiles,
        initialPlanTuitions,
    });
};

type SubmissionDataArgs = {
    generateUUID: () => string;
    siteId: string;
    state: Types.State;
    adhocPurchasePricingGroups: null | AdhocPurchasePricingGroups;
    pricingGroupOptions: Array<Types.PricingGroupOption>;
    billingProfiles: Array<Types.BillingProfile>;
    initialPlanTuitions?: Types.Plan['tuitions'];
};
type SubmissionData = {
    type: 'data';
    isEmptyPlan: boolean;
    automaticInvoicingConfigs: ReturnType<typeof automaticInvoicingData>;
    customStartPeriod: null | ReturnType<typeof customStartPeriodData>;
    plans: ReturnType<typeof plansData>;
    customEndPeriod: null | ReturnType<typeof customEndPeriodData>;
    tuitionsWithOverriddenPrice: null | TuitionsWithOverriddenPrice;
    template: null | ApiTypes.ApiTemplateInput;
    childIds: Array<Types.Child['id']>;
};

type Error = {type: 'error'; error: {message: string}};

type CustomPeriodCommon = {
    plans: ExecutePlansData;
    adhocPurchases: ExecuteTransformChildrenAndFeeToApiAdhocPurchases;
    billingProfiles: Array<Types.BillingProfile>;
};
type CustomStartPeriodData = CustomPeriodCommon & {
    customStartPeriodProratedProducts: Array<ApiTypes.ApiProductCreation>;
    customStartPeriodProratedSessions: Array<ApiTypes.ApiSessionPackageCreation>;
};
type CustomEndPeriodData = CustomPeriodCommon & {
    customEndPeriodProratedProducts: Array<ApiTypes.ApiProductCreation>;
    customEndPeriodProratedSessions: Array<ApiTypes.ApiSessionPackageCreation>;
};

type AutomaticInvoicingData = UpdateBillingFrequenciesMutationVariables &
    EnableBillPayerAutomaticInvoiceSendingMutationVariables & {
        shouldEnableAutomaticInvoicing: boolean;
        siteSetId: string;
    };

type TuitionsWithOverriddenPrice = {
    overriddenPriceTuitions: Array<{newTuition: ApiTypes.ApiSessionPackageCreation; oldTuitionId: Types.Tuition['id']}>;
};

const submissionData = ({
    siteId,
    state,
    adhocPurchasePricingGroups: maybeAdhocPurchasePricingGroups,
    pricingGroupOptions,
    billingProfiles,
    generateUUID,
    initialPlanTuitions,
}: SubmissionDataArgs): Error | SubmissionData => {
    const {
        tuitionsState: {selectedTuitions, allSelectedTuitionsHaveTheSameFrequency},
        feesState: {selectedFees},
        billingFrequencyState: {selectedBillingFrequency, billingFrequencies},
        billingPeriodState: {selectedBillingPeriod},
        childrenState: {selectedChildren, allSelectedChildrenHaveBillPayer},
        discountState: {selectedDiscounts},
        customPeriodState: {customPeriodDates: maybeCustomPeriodDates},
        pricingGroupState: {selectedPricingGroup},
        endDateState: {selectedEndDate: maybeSelectedEndDate},
        template: {shouldAddNewTemplate, newTemplateName},
        isAutomaticBillingEnabled,
        isAutomaticDecouplingEnabled,
        view,
    } = state;

    const customPeriodFlags = getCustomPeriodFlags({
        selectedBillingPeriod,
        customPeriodDates: maybeCustomPeriodDates,
        selectedEndDate: maybeSelectedEndDate,
        adhocPurchasePricingGroups: maybeAdhocPurchasePricingGroups,
        selectedTuitions,
        selectedFees,
        biweeklyBillingStartDate: biweeklyBillingStartDate({billingFrequencies}),
        selectedBillingFrequencyType: selectedBillingFrequency?.type ?? null,
    });

    if (customPeriodFlags.type === 'error') {
        return {type: 'error', error: {message: 'Custom periods are not available'}};
    }

    const {type: customPeriodType, customPeriodDates, selectedEndDate, adhocPurchasePricingGroups} = customPeriodFlags;

    const viewSpecificConfig = getViewSpecificConfig({
        state,
        maybeAdhocPurchasePricingGroups,
    });

    if (viewSpecificConfig.type === 'error') {
        return {type: 'error', error: {message: 'View specific information is not available'}};
    }

    const {planStartDate, planEndDate} = viewSpecificConfig;

    const isEmptyPlan =
        state.emptyPlan.enabled &&
        isNonEmpty(selectedChildren) &&
        !isNonEmpty(state.tuitionsState.selectedTuitions) &&
        billingProfiles.length > 0;

    const isNonEmptyPlanInvalid =
        !isNonEmpty(selectedTuitions) ||
        !isNonEmpty(selectedChildren) ||
        (!hasValue(selectedBillingPeriod) && view === 'create') ||
        !hasValue(planStartDate) ||
        !hasValue(selectedBillingFrequency) ||
        !hasValue(selectedPricingGroup) ||
        !allSelectedChildrenHaveBillPayer ||
        !allSelectedTuitionsHaveTheSameFrequency;

    const isSubmissionDisabled = isNonEmptyPlanInvalid && !isEmptyPlan;

    if (isSubmissionDisabled) {
        return {type: 'error', error: {message: 'Necessary information is not available'}};
    }

    if (!isEmptyPlan && !isNonEmptyPlanInvalid) {
        const pricingGroupsWithoutAutomatic = pricingGroupOptions.filter(
            pricingGroup => !isAutomaticPricingGroup(pricingGroup),
        );

        /**
         * To create plans, the pricing group needs special treatment so we use a separate value for adhoc purchases
         * Please refer to the transformer function docs to see how pricing groups for plans are treated
         */
        const pricingGroupIdForPlan = transformPlanPricingGroupIdToApiPlanPricingGroupId(selectedPricingGroup);
        const billingProfile = selectedTuitions[0].billingProfile;

        const tuitionsWithOverriddenPrice = selectedTuitions.filter(tuition => tuition.overriddenPrice);

        return {
            type: 'data',
            childIds: selectedChildren.map(child => child.id),
            isEmptyPlan: false,
            automaticInvoicingConfigs: automaticInvoicingData({
                siteId,
                selectedBillingFrequency,
                selectedChildren,
                isAutomaticDecouplingEnabled,
                isAutomaticBillingEnabled,
            }),
            customStartPeriod:
                customPeriodType === 'custom-start-and-end-period' || customPeriodType === 'custom-start-period'
                    ? customStartPeriodData({
                          generateUUID,
                          siteId,
                          selectedChildren,
                          selectedTuitions,
                          selectedFees,
                          selectedPricingGroup,
                          customPeriodDates,
                          pricingGroupsWithoutAutomatic,
                          billingProfile,
                          pricingGroupIdForPlan,
                          pricingGroupsForChildren: adhocPurchasePricingGroups.pricingGroupsByChildrenForStartDate,
                          billingProfiles,
                          view,
                          initialPlanTuitions,
                      })
                    : null,
            plans: plansData({
                generateUUID,
                selectedChildren,
                selectedFees,
                endDate: planEndDate,
                selectedDiscounts,
                pricingGroupId: pricingGroupIdForPlan,
                startDate: planStartDate,
                billingProfile,
                view,
                initialPlanTuitions,
            }),
            customEndPeriod:
                customPeriodType === 'custom-start-and-end-period' || customPeriodType === 'custom-end-period'
                    ? customEndPeriodData({
                          generateUUID,
                          selectedChildren,
                          selectedEndDate,
                          selectedFees,
                          selectedPricingGroup,
                          selectedTuitions,
                          siteId,
                          pricingGroupsWithoutAutomatic,
                          billingProfile,
                          startDate:
                              getStartDateForEndPeriod(
                                  selectedBillingFrequency,
                                  selectedEndDate,
                                  biweeklyBillingStartDate({billingFrequencies}),
                              )?.format('YYYY-MM-DD') ?? '',
                          pricingGroupIdForPlan,
                          pricingGroupsForChildren: adhocPurchasePricingGroups.pricingGroupsByChildrenForEndDate,
                          billingProfiles,
                          view,
                          initialPlanTuitions,
                      })
                    : null,
            tuitionsWithOverriddenPrice: isNonEmpty(tuitionsWithOverriddenPrice)
                ? tuitionsWithOverriddenPriceData(tuitionsWithOverriddenPrice, siteId)
                : null,
            template:
                shouldAddNewTemplate && hasValue(newTemplateName)
                    ? {
                          siteId,
                          name: newTemplateName,
                          tuitionIds: selectedTuitions.map(tuition => tuition.id),
                          feeIds: selectedFees.map(fee => fee.id),
                          discountIds: selectedDiscounts.map(discount => discount.id),
                          billingFrequency:
                              // We are using 'monthly' as a default value for billing frequency temporarily until we remove the 'none' option
                              selectedBillingFrequency.type === 'none' ? 'monthly' : selectedBillingFrequency.type,
                          pricingGroupId: pricingGroupIdForPlan,
                      }
                    : null,
        };
    } else {
        const endDate = hasValue(state.emptyPlan.endDate)
            ? transformEndDateToPlanEndDate(state.emptyPlan.endDate)
            : null;

        return {
            type: 'data',
            childIds: selectedChildren.map(child => child.id),
            isEmptyPlan: true,
            automaticInvoicingConfigs: null,
            customStartPeriod: null,
            customEndPeriod: null,
            plans: plansData({
                generateUUID,
                selectedChildren,
                selectedFees,
                startDate: state.emptyPlan.startDate.format('YYYY-MM-DD'),
                endDate,
                selectedDiscounts,
                pricingGroupId: null,
                billingProfile: billingProfiles[0],
                view,
                initialPlanTuitions,
            }),
            tuitionsWithOverriddenPrice: null,
            template: null,
        };
    }
};

const automaticInvoicingData = ({
    siteId,
    selectedBillingFrequency,
    selectedChildren,
    isAutomaticDecouplingEnabled,
    isAutomaticBillingEnabled,
}: {
    siteId: string;
    selectedBillingFrequency: Types.State['billingFrequencyState']['selectedBillingFrequency'];
    selectedChildren: Types.State['childrenState']['selectedChildren'];
    isAutomaticDecouplingEnabled: boolean;
    isAutomaticBillingEnabled: boolean;
}): null | Array<AutomaticInvoicingData> => {
    const invoicingFrequency = selectedBillingFrequency
        ? transformBillingFrequencyToApiSchedule(selectedBillingFrequency)
        : null;
    const automaticInvoicingConfig = invoicingFrequency
        ? selectedChildren.map(child => {
              return {
                  siteSetId: siteId,
                  billPayerIds: child.billPayers.map(billPayer => billPayer.id),
                  invoicingFrequency,
                  shouldEnableAutomaticInvoicing: isAutomaticDecouplingEnabled
                      ? isAutomaticBillingEnabled && !child.hasPlans
                      : true,
              };
          })
        : null;

    return automaticInvoicingConfig;
};

const customStartPeriodData = ({
    generateUUID,
    siteId,
    selectedChildren,
    selectedTuitions,
    selectedFees,
    selectedPricingGroup,
    customPeriodDates,
    pricingGroupsWithoutAutomatic,
    billingProfile,
    pricingGroupIdForPlan,
    pricingGroupsForChildren,
    billingProfiles,
    view,
    initialPlanTuitions,
}: {
    generateUUID: () => string;
    siteId: string;
    selectedChildren: Types.State['childrenState']['selectedChildren'];
    selectedTuitions: Types.State['tuitionsState']['selectedTuitions'];
    selectedFees: Types.State['feesState']['selectedFees'];
    selectedPricingGroup: Types.State['pricingGroupState']['selectedPricingGroup'];
    customPeriodDates: NonNullable<Types.State['customPeriodState']['customPeriodDates']>;
    pricingGroupsWithoutAutomatic: Array<Types.PricingGroupOption>;
    billingProfile: Types.BillingProfile;
    pricingGroupIdForPlan: null | Types.PricingGroup['id'];
    pricingGroupsForChildren: Array<PricingGroupsForChild>;
    billingProfiles: Array<Types.BillingProfile>;
    view: Types.State['view'];
    initialPlanTuitions?: Types.Plan['tuitions'];
}): CustomStartPeriodData => {
    const customStartPeriodProratedItems: Array<Types.ProratedItemWithBillingType> = [
        selectedTuitions.map(tuition =>
            tuition.customStartPeriod ? {type: tuition.type, ...tuition.customStartPeriod} : null,
        ),
        selectedFees.map(fee => (fee.customStartPeriod ? {type: fee.type, ...fee.customStartPeriod} : null)),
    ]
        .flat()
        .filter(hasValue);
    const customStartPeriodProratedProducts = transformProratedItemsToApiProductCreations({
        siteId,
        proratedItems: customStartPeriodProratedItems,
        pricingGroups: pricingGroupsWithoutAutomatic,
        selectedPricingGroupId: selectedPricingGroup,
        suffix: ` ${i18next.t('react.simplifiedUsBillingPlan.translations.json.customPeriod.firstInvoice')}`,
    });
    const customStartPeriodProratedSessions = transformProratedTuitionsToApiSessionPackageCreations(
        selectedTuitions,
        siteId,
        pricingGroupsWithoutAutomatic,
        selectedPricingGroup,
        'start',
    );

    return {
        customStartPeriodProratedProducts,
        customStartPeriodProratedSessions,
        plans: plansData({
            generateUUID,
            selectedChildren,
            selectedDiscounts: [],
            selectedFees: [],
            startDate: customPeriodDates.startDate.format('YYYY-MM-DD'),
            endDate: transformEndDateToPlanEndDate(customPeriodDates.endDate),
            billingProfile,
            pricingGroupId: pricingGroupIdForPlan,
            view,
            initialPlanTuitions,
        }),
        adhocPurchases: transformChildrenAndFeeToApiAdhocPurchases(
            pricingGroupsForChildren,
            customPeriodDates.startDate,
        ),
        billingProfiles,
    };
};

type PlansData = SetUpPlansData;
type SetUpPlansData = (args: {
    generateUUID: () => string;
    selectedChildren: Types.State['childrenState']['selectedChildren'];
    selectedFees: Types.State['feesState']['selectedFees'];
    selectedDiscounts: Types.State['discountState']['selectedDiscounts'];
    endDate: null | string;
    startDate: string;
    billingProfile: Types.BillingProfile;
    pricingGroupId: null | Types.PricingGroup['id'];
    view: Types.State['view'];
    initialPlanTuitions?: Types.Plan['tuitions'];
}) => ExecutePlansData;
type ExecutePlansData = (args: {
    selectedTuitions: Types.State['tuitionsState']['selectedTuitions'];
}) => Array<ApiTypes.ApiPlanInput>;

const plansData: PlansData =
    ({
        generateUUID,
        selectedChildren,
        selectedFees,
        selectedDiscounts,
        endDate,
        startDate,
        billingProfile,
        pricingGroupId,
        view,
        initialPlanTuitions,
    }) =>
    ({selectedTuitions}) => {
        const plans = selectedChildren.map(child => ({
            input: {
                plan: {
                    id: generateUUID(),
                    childId: child.id,
                    pricingGroupId,
                    note: '',
                    from: startDate,
                    to: endDate,
                    planParts: [
                        {
                            /**
                             * The ordering value is used to determine the order of the plan parts when resolving this mutation.
                             * However in this flow we only ever have one plan part so the ordering can be set to 0.
                             */
                            ordering: 0,
                            planPartId: generateUUID(),
                            billingProfileId: billingProfile.id,
                            sessionBookings:
                                view === 'edit' && hasValue(initialPlanTuitions)
                                    ? transformTuitionsToApiSessionBookingsForEditView(
                                          selectedTuitions,
                                          initialPlanTuitions,
                                      )
                                    : transformTuitionsToApiSessionBookings(selectedTuitions),
                            productBookings: transformFeesToApiProductBookings(selectedFees),
                            billing: {
                                id: billingProfile.billingScheme.id,
                                title: billingProfile.billingScheme.title,
                                // In order to have full year billing, we must set
                                // 'invoices' to 12 (12 months in a year)
                                // 'weeksOfCare' to 52 (52 weeks in a year)
                                invoices: 12,
                                weeksOfCare: 52,
                            },
                            termTimeOnly: false,
                            attendanceScheduleId: billingProfile.attendanceScheduleId ?? '',
                            discounts: transformDiscountsToApiDiscounts(sortDiscounts(selectedDiscounts)),
                        },
                    ],
                },
            },
        }));

        return plans;
    };

const customEndPeriodData = ({
    generateUUID,
    siteId,
    selectedChildren,
    selectedEndDate,
    selectedTuitions,
    selectedFees,
    selectedPricingGroup,
    pricingGroupsWithoutAutomatic,
    billingProfile,
    startDate,
    pricingGroupIdForPlan,
    pricingGroupsForChildren,
    billingProfiles,
    view,
    initialPlanTuitions,
}: {
    generateUUID: () => string;
    siteId: string;
    selectedChildren: Types.State['childrenState']['selectedChildren'];
    selectedEndDate: NonNullable<Types.State['endDateState']['selectedEndDate']>;
    selectedTuitions: Types.State['tuitionsState']['selectedTuitions'];
    selectedFees: Types.State['feesState']['selectedFees'];
    selectedPricingGroup: Types.State['pricingGroupState']['selectedPricingGroup'];
    pricingGroupsWithoutAutomatic: Array<Types.PricingGroupOption>;
    billingProfile: Types.BillingProfile;
    startDate: string;
    pricingGroupIdForPlan: null | Types.PricingGroup['id'];
    pricingGroupsForChildren: Array<PricingGroupsForChild>;
    billingProfiles: Array<Types.BillingProfile>;
    view: Types.State['view'];
    initialPlanTuitions?: Types.Plan['tuitions'];
}): CustomEndPeriodData => {
    const customEndPeriodProratedItems: Array<Types.ProratedItemWithBillingType> = [
        selectedTuitions.map(tuition =>
            tuition.customEndPeriod ? {type: tuition.type, ...tuition.customEndPeriod} : null,
        ),
        selectedFees.map(fee => (fee.customEndPeriod ? {type: fee.type, ...fee.customEndPeriod} : null)),
    ]
        .flat()
        .filter(hasValue);

    const customEndPeriodProratedProducts = transformProratedItemsToApiProductCreations({
        siteId,
        proratedItems: customEndPeriodProratedItems,
        pricingGroups: pricingGroupsWithoutAutomatic,
        selectedPricingGroupId: selectedPricingGroup,
        suffix: ` ${i18next.t('react.simplifiedUsBillingPlan.translations.json.customPeriod.lastInvoice')}`,
    });
    const customEndPeriodProratedSessions = transformProratedTuitionsToApiSessionPackageCreations(
        selectedTuitions,
        siteId,
        pricingGroupsWithoutAutomatic,
        selectedPricingGroup,
        'end',
    );

    return {
        customEndPeriodProratedProducts,
        customEndPeriodProratedSessions,
        plans: plansData({
            generateUUID,
            selectedChildren,
            selectedDiscounts: [],
            selectedFees: [],
            endDate: transformEndDateToPlanEndDate(selectedEndDate),
            startDate,
            billingProfile,
            pricingGroupId: pricingGroupIdForPlan,
            view,
            initialPlanTuitions,
        }),
        adhocPurchases: transformChildrenAndFeeToApiAdhocPurchases(pricingGroupsForChildren, selectedEndDate),
        billingProfiles,
    };
};

const tuitionsWithOverriddenPriceData = (
    tuitions: NonEmptyArray<Types.Tuition>,
    siteId: string,
): TuitionsWithOverriddenPrice => {
    return {
        overriddenPriceTuitions: transformTuitionsWithOverriddenPriceToApiSessionPackageCreation(tuitions, siteId),
    };
};

export const getCustomPeriodFlags = ({
    selectedBillingPeriod,
    customPeriodDates,
    selectedEndDate,
    adhocPurchasePricingGroups,
    selectedTuitions,
    selectedFees,
    biweeklyBillingStartDate,
    selectedBillingFrequencyType,
}: {
    selectedBillingPeriod: Types.State['billingPeriodState']['selectedBillingPeriod'];
    customPeriodDates: Types.State['customPeriodState']['customPeriodDates'];
    selectedEndDate: Types.State['endDateState']['selectedEndDate'];
    adhocPurchasePricingGroups: null | AdhocPurchasePricingGroups;
    selectedTuitions: Types.State['tuitionsState']['selectedTuitions'];
    selectedFees: Types.State['feesState']['selectedFees'];
    biweeklyBillingStartDate: Moment;
    selectedBillingFrequencyType: Types.BillingFrequencyOption['type'] | null;
}):
    | {
          type: 'custom-start-and-end-period' | 'custom-start-period-and-end-date-last-day-of-period';
          customPeriodDates: NonNullable<Types.State['customPeriodState']['customPeriodDates']>;
          selectedEndDate: NonNullable<Types.State['endDateState']['selectedEndDate']>;
          adhocPurchasePricingGroups: AdhocPurchasePricingGroups;
      }
    | {
          type: 'custom-start-period';
          customPeriodDates: NonNullable<Types.State['customPeriodState']['customPeriodDates']>;
          selectedEndDate: null;
          adhocPurchasePricingGroups: AdhocPurchasePricingGroups;
      }
    | {
          type: 'custom-end-period';
          customPeriodDates: null;
          selectedEndDate: NonNullable<Types.State['endDateState']['selectedEndDate']>;
          adhocPurchasePricingGroups: AdhocPurchasePricingGroups;
      }
    | {
          type: 'end-date-last-day-of-period';
          customPeriodDates: null;
          selectedEndDate: NonNullable<Types.State['endDateState']['selectedEndDate']>;
          adhocPurchasePricingGroups: null;
      }
    | {
          type: 'no-custom-period';
          customPeriodDates: null;
          selectedEndDate: null;
          adhocPurchasePricingGroups: null;
      }
    | {
          type: 'error';
      } => {
    const customEndDate = getCustomEndDate({
        selectedEndDate,
        selectedTuitions,
        selectedFees,
        adhocPurchasePricingGroups,
        biweeklyBillingStartDate,
        selectedBillingFrequencyType,
    });

    const hasCustomStartPeriod =
        selectedBillingPeriod?.type === 'custom' &&
        hasValue(customPeriodDates) &&
        hasValue(selectedBillingFrequencyType) &&
        !isFirstDayOfBillingPeriod(
            customPeriodDates.startDate,
            selectedBillingFrequencyType,
            biweeklyBillingStartDate,
        ) &&
        hasValue(adhocPurchasePricingGroups?.pricingGroupsByChildrenForStartDate);

    if (customEndDate.type === 'error') {
        return {type: 'error'};
    }

    if (hasCustomStartPeriod && customEndDate.type === 'custom-end-period') {
        return {
            type: 'custom-start-and-end-period',
            customPeriodDates,
            selectedEndDate: customEndDate.selectedEndDate,
            adhocPurchasePricingGroups,
        };
    }

    if (hasCustomStartPeriod && customEndDate.type === 'end-date-last-day-of-period') {
        return {
            type: 'custom-start-period-and-end-date-last-day-of-period',
            customPeriodDates,
            selectedEndDate: customEndDate.selectedEndDate,
            adhocPurchasePricingGroups,
        };
    }

    if (hasCustomStartPeriod && customEndDate.type === 'no-end-date') {
        return {
            type: 'custom-start-period',
            customPeriodDates,
            selectedEndDate: null,
            adhocPurchasePricingGroups,
        };
    }

    if (!hasCustomStartPeriod && customEndDate.type === 'custom-end-period') {
        return {
            type: 'custom-end-period',
            customPeriodDates: null,
            selectedEndDate: customEndDate.selectedEndDate,
            adhocPurchasePricingGroups: customEndDate.adhocPurchasePricingGroups,
        };
    }

    if (!hasCustomStartPeriod && customEndDate.type === 'end-date-last-day-of-period') {
        return {
            type: 'end-date-last-day-of-period',
            customPeriodDates: null,
            selectedEndDate: customEndDate.selectedEndDate,
            adhocPurchasePricingGroups: null,
        };
    }

    return {
        type: 'no-custom-period',
        customPeriodDates: null,
        selectedEndDate: null,
        adhocPurchasePricingGroups: null,
    };
};

export const getCustomEndDate = ({
    selectedEndDate,
    selectedTuitions,
    selectedFees,
    adhocPurchasePricingGroups,
    biweeklyBillingStartDate,
    selectedBillingFrequencyType,
}: {
    selectedEndDate: Types.State['endDateState']['selectedEndDate'];
    selectedTuitions: Types.State['tuitionsState']['selectedTuitions'];
    selectedFees: Types.State['feesState']['selectedFees'];
    adhocPurchasePricingGroups: null | AdhocPurchasePricingGroups;
    biweeklyBillingStartDate: Moment;
    selectedBillingFrequencyType: Types.BillingFrequencyOption['type'] | null;
}):
    | {
          type: 'custom-end-period';
          selectedEndDate: NonNullable<Types.State['endDateState']['selectedEndDate']>;
          adhocPurchasePricingGroups: AdhocPurchasePricingGroups;
      }
    | {
          type: 'end-date-last-day-of-period';
          selectedEndDate: NonNullable<Types.State['endDateState']['selectedEndDate']>;
      }
    | {
          type: 'no-end-date';
          selectedEndDate: null;
      }
    | {
          type: 'error';
      } => {
    const isSelectedEndDateLastDayOfPeriod =
        hasValue(selectedEndDate) &&
        hasValue(selectedBillingFrequencyType) &&
        isEndOfPeriodDate(selectedEndDate, selectedBillingFrequencyType, biweeklyBillingStartDate);

    const hasCustomEndPeriod =
        hasValue(selectedEndDate) &&
        isNonEmpty(selectedTuitions) &&
        selectedTuitions.every(tuition => hasValue(tuition.customEndPeriod)) &&
        selectedFees.every(fee => hasValue(fee.customEndPeriod)) &&
        !isSelectedEndDateLastDayOfPeriod &&
        hasValue(adhocPurchasePricingGroups?.pricingGroupsByChildrenForEndDate);

    if (isSelectedEndDateLastDayOfPeriod && !hasCustomEndPeriod) {
        return {
            type: 'end-date-last-day-of-period',
            selectedEndDate,
        };
    }

    if (!isSelectedEndDateLastDayOfPeriod && hasCustomEndPeriod) {
        return {
            type: 'custom-end-period',
            selectedEndDate,
            adhocPurchasePricingGroups,
        };
    }

    if (!isSelectedEndDateLastDayOfPeriod && !hasCustomEndPeriod) {
        return {
            type: 'no-end-date',
            selectedEndDate: null,
        };
    }

    return {type: 'error'};
};

export const getViewSpecificConfig = ({
    state,
    maybeAdhocPurchasePricingGroups,
}: {
    state: Types.State;
    maybeAdhocPurchasePricingGroups: null | AdhocPurchasePricingGroups;
}): {type: 'error'} | {type: 'data'; planStartDate: null | string; planEndDate: null | string} => {
    const {
        view,
        billingPeriodState: {selectedBillingPeriod},
        customPeriodState: {customPeriodDates: maybeCustomPeriodDates},
        endDateState: {selectedEndDate: maybeSelectedEndDate},
        tuitionsState: {selectedTuitions},
        feesState: {selectedFees},
        billingFrequencyState: {selectedBillingFrequency, billingFrequencies},
    } = state;

    const customPeriodFlags = getCustomPeriodFlags({
        selectedBillingPeriod,
        customPeriodDates: maybeCustomPeriodDates,
        selectedEndDate: maybeSelectedEndDate,
        adhocPurchasePricingGroups: maybeAdhocPurchasePricingGroups,
        selectedTuitions,
        selectedFees,
        biweeklyBillingStartDate: biweeklyBillingStartDate({billingFrequencies}),
        selectedBillingFrequencyType: selectedBillingFrequency?.type ?? null,
    });

    if (customPeriodFlags.type === 'error') {
        return {type: 'error'};
    }

    const {type: customPeriodType, customPeriodDates, selectedEndDate} = customPeriodFlags;

    switch (view) {
        case 'create': {
            const planStartDate =
                (customPeriodType === 'custom-start-and-end-period' || customPeriodType === 'custom-start-period'
                    ? transformEndDateToPlanEndDate(customPeriodDates.endDate)
                    : selectedBillingPeriod?.unformattedStartDate?.format('YYYY-MM-DD')) ?? null;

            const planEndDate = (() => {
                if (customPeriodType === 'custom-start-and-end-period' || customPeriodType === 'custom-end-period') {
                    return (
                        getStartDateForEndPeriod(
                            selectedBillingFrequency,
                            selectedEndDate,
                            biweeklyBillingStartDate({billingFrequencies}),
                        )?.format('YYYY-MM-DD') ?? null
                    );
                }
                if (
                    customPeriodType === 'custom-start-period-and-end-date-last-day-of-period' ||
                    customPeriodType === 'end-date-last-day-of-period'
                ) {
                    return transformEndDateToPlanEndDate(selectedEndDate);
                }
                return null;
            })();

            return {type: 'data', planStartDate, planEndDate};
        }
        case 'edit': {
            if (!hasValue(state.planDateRange)) {
                return {type: 'error'};
            }
            const planStartDate = moment(state.planDateRange.startDate).format('YYYY-MM-DD');
            const planEndDate = state.planDateRange.endDate
                ? moment(state.planDateRange.endDate).add(1, 'day').format('YYYY-MM-DD')
                : null;

            return {type: 'data', planStartDate, planEndDate};
        }
        default:
            exhaustiveCheck(view);
            return {type: 'error'};
    }
};

export {submissionData, useSubmissionData};
export type {SubmissionData, AutomaticInvoicingData, TuitionsWithOverriddenPrice};
